import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from "d3";
import { Skeleton } from "@mui/material";
import { getList } from "../../common/BackendProvider.jsx";
import { toast } from "react-toastify";
import { SearchBar } from './SearchBar.jsx';
import { Pagination } from '../../layout/Pagination.jsx';

const numberFormat = format(",.0f");
const priceFormat = format(",.2f");

const scoreTone = (value) => {
    if (value > 5) return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (value > 2) return "bg-lime-100 text-lime-700 border-lime-200";
    if (value > -1) return "bg-amber-100 text-amber-700 border-amber-200";
    if (value > -4) return "bg-orange-100 text-orange-700 border-orange-200";
    return "bg-red-100 text-red-700 border-red-200";
};

const formatScore = (value) => {
    const numericValue = Number(value ?? 0);
    return `${numericValue > 0 ? "+" : ""}${numberFormat(numericValue)}`;
};

const formatPrice = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? priceFormat(numericValue) : "-";
};

const formatMarketCap = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0
        ? new Intl.NumberFormat('en', {
            notation: 'compact',
            maximumFractionDigits: 2,
        }).format(numericValue)
        : "-";
};

const formatEmployees = (value) => {
    const numericValue = Number(String(value ?? "").replace(/[^0-9]/g, ""));
    return Number.isFinite(numericValue) && numericValue > 0 ? numberFormat(numericValue) : "-";
};

const profileValue = (value) => {
    return value === null || value === undefined || value === "" ? "-" : value;
};

const getLogoUrl = (company) => {
    if (company?.logo_url) return company.logo_url;
    if (!company?.website) return null;
    const website = company.website.startsWith("http") ? company.website : `https://${company.website}`;
    try {
        const host = new URL(website).hostname;
        return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
    } catch (e) {
        return null;
    }
};

const CompanyLogo = ({ company }) => {
    const logoUrl = getLogoUrl(company);
    const symbol = company?.symbol || "?";
    const [logoFailed, setLogoFailed] = useState(false);

    return (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            {logoUrl && !logoFailed ? (
                <img
                    src={logoUrl}
                    alt=""
                    className="h-full w-full object-contain p-1"
                    onError={() => setLogoFailed(true)}
                />
            ) : null}
            {(!logoUrl || logoFailed) ? <span className="text-sm font-bold text-emerald-600">
                {symbol.slice(0, 2)}
            </span> : null}
        </div>
    );
};

const ScorePill = ({ label, value }) => (
    <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</div>
        <div className="mt-1 text-sm font-bold text-slate-900">{formatScore(value)}</div>
    </div>
);

const CoverageBar = ({ value, fallbackValue }) => {
    const numericValue = Number(value);
    const coverage = Number.isFinite(numericValue)
        ? Math.max(0, Math.min(100, numericValue))
        : fallbackValue === 1 ? 100 : fallbackValue === 0 ? 67 : 0;

    return (
        <div>
            <div className="mb-1 flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
                <span>Coverage</span>
                <span>{coverage}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${coverage}%` }} />
            </div>
        </div>
    );
};

const MetricCard = ({ label, value, detail }) => (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
        <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
        {detail ? <div className="mt-1 text-sm font-semibold text-slate-500">{detail}</div> : null}
    </div>
);

const sortOptions = [
    { id: "data_completeness_score:desc", name: "Most complete data" },
    { id: "data_completeness_score:asc", name: "Least complete data" },
    { id: "name:asc", name: "Company name A-Z" },
    { id: "name:desc", name: "Company name Z-A" },
    { id: "last_price:desc", name: "Highest last price" },
    { id: "last_price:asc", name: "Lowest last price" },
    { id: "change_24h_percent:desc", name: "Strongest daily move" },
    { id: "change_24h_percent:asc", name: "Weakest daily move" },
];

const LoadingCards = () => (
    <div className="grid gap-4 xl:grid-cols-2">
        {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <Skeleton variant="rectangular" height={120} sx={{ bgcolor: 'grey.50' }} />
            </div>
        ))}
    </div>
);

const CompanyCard = ({ company }) => {
    const totalScore = Number(company.total_score ?? 0);
    const isPositiveMove = Number(company.change_24h_percent ?? 0) >= 0;
    const description = company.short_description || company.description;
    const website = company.website || company.domain;
    const websiteHref = website && website !== "-"
        ? (website.startsWith("http") ? website : `https://${website}`)
        : null;

    return (
        <Link
            to={`/company/${company.symbol}`}
            className="group block rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                    <CompanyLogo company={company} />
                    <div className="min-w-0">
                        <div className="truncate text-lg font-bold text-slate-950 group-hover:text-emerald-700">
                            {company.name}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-500">
                            <span>{company.symbol}</span>
                            {company.sector?.name ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{company.sector.name}</span> : null}
                            {company.industry?.name ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{company.industry.name}</span> : null}
                            {company.country?.name ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{company.country.name}</span> : null}
                        </div>
                    </div>
                </div>
                <div className={`shrink-0 rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(totalScore)}`}>
                    {formatScore(totalScore)}
                </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Last Price</div>
                    <div className="mt-1 text-base font-bold text-slate-950">{formatPrice(company.last_price)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Market Cap</div>
                    <div className="mt-1 text-base font-bold text-slate-950">{formatMarketCap(company.market_cap)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Daily Move</div>
                    <div className={`mt-1 text-base font-bold ${isPositiveMove ? "text-emerald-600" : "text-red-600"}`}>
                        {Number(company.change_24h_percent ?? 0).toFixed(2)}%
                    </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Volume</div>
                    <div className="mt-1 text-base font-bold text-slate-950">{formatPrice(company.volume_24h)}</div>
                </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Exchange</div>
                    <div className="mt-1 text-sm font-bold text-slate-950">{company.exchange?.code || profileValue(company.exchange)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Employees</div>
                    <div className="mt-1 text-sm font-bold text-slate-950">{formatEmployees(company.employees)}</div>
                </div>
            </div>

            {description ? (
                <p className="mt-4 line-clamp-2 text-sm font-medium leading-6 text-slate-600">
                    {description}
                </p>
            ) : null}

            {websiteHref ? (
                <div className="mt-4 text-sm font-bold text-emerald-700">
                    {websiteHref.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                </div>
            ) : null}

            <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <ScorePill label="ESG" value={company.esg_score} />
                <ScorePill label="Financial" value={company.financial_score} />
                <ScorePill label="Risk" value={company.risk_score} />
            </div>

            <div className="mt-4">
                <CoverageBar value={company.data_completeness_score} fallbackValue={company.data_completeness_flags} />
            </div>
        </Link>
    );
};

export const Companies = () => {
    const [companies, setCompanies] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({"profile": "basic", "hasprice": 0, "sector": 0, "industry": 0, "region": 0, "size": 0});
    const [sort, setSort] = useState({field: "data_completeness_score", order: "desc"});

    const loadCompanies = () => {
        setLoading(true);
        getList("companies", {filter: filter, sort: sort, pagination:{page:page, perPage:100}}).then((response) => {
            setCompanies(response.data.companies || []);
            setPages(response.data.pages || 1);
            setCount(response.data.count || 0);
            setLoading(false);
        }).catch(() => {
            toast.error("Error while loading companies", {});
            setLoading(false);
        });
    };

    useEffect(() => {
        loadCompanies();
    }, [filter, sort, page]);

    const setFilterValue = (key, value) => {
        setPage(1);
        setFilter((current) => ({...current, [key]: value}));
    };

    const handleSortChange = (event) => {
        const [field, order] = event.target.value.split(":");
        setPage(1);
        setSort({field, order});
    };

    const stats = useMemo(() => {
        const priced = companies.filter((company) => Number(company.last_price) > 0).length;
        const scored = companies.filter((company) => company.total_score !== null && company.total_score !== undefined).length;
        const completenessValues = companies
            .map((company) => Number(company.data_completeness_score))
            .filter((value) => Number.isFinite(value));
        const averageScore = scored
            ? companies.reduce((sum, company) => sum + Number(company.total_score || 0), 0) / scored
            : 0;
        const averageCompleteness = completenessValues.length
            ? completenessValues.reduce((sum, value) => sum + value, 0) / completenessValues.length
            : 0;

        return {
            priced,
            scored,
            averageScore,
            averageCompleteness,
            activeFilters: Object.entries(filter).filter(([key, value]) => key !== "profile" && Number(value) > 0).length,
        };
    }, [companies, filter]);

    return (
        <div className="space-y-5 p-4 lg:p-8">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-600">Research universe</div>
                    <h1 className="mt-2 text-3xl font-bold text-slate-950">Company Discovery</h1>
                    <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
                        Compare public companies by market signal, score coverage, and financial readiness from one workspace.
                    </p>
                </div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
                    {stats.activeFilters ? `${stats.activeFilters} active filter${stats.activeFilters > 1 ? "s" : ""}` : "Profile-ready operating companies"}
                </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <MetricCard label="Companies" value={count || companies.length} detail={`${companies.length} profile-ready shown`} />
                <MetricCard label="Market Data" value={stats.priced} detail="companies with prices" />
                <MetricCard label="Completeness" value={`${numberFormat(stats.averageCompleteness)}%`} detail="average visible coverage" />
                <MetricCard label="Avg Score" value={formatScore(stats.averageScore)} detail="visible companies" />
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-end md:justify-between">
                    <div>
                        <div className="text-sm font-bold text-slate-950">Sort results</div>
                        <div className="text-xs font-semibold text-slate-500">Rank the visible universe by readiness, name, price, or daily move.</div>
                    </div>
                    <label className="flex min-w-[240px] flex-col text-left text-sm font-semibold text-slate-600">
                        Sort by
                        <select
                            value={`${sort.field}:${sort.order}`}
                            onChange={handleSortChange}
                            className="mt-1 rounded-lg border border-[#D9E1EC] bg-white p-2 text-slate-950"
                        >
                            {sortOptions.map((option) => (
                                <option key={option.id} value={option.id}>{option.name}</option>
                            ))}
                        </select>
                    </label>
                </div>
                <SearchBar
                    setSector={(value) => setFilterValue("sector", value)}
                    setIndustry={(value) => setFilterValue("industry", value)}
                    setRegion={(value) => setFilterValue("region", value)}
                    setSize={(value) => setFilterValue("size", value)}
                />
            </div>

            {loading ? (
                <LoadingCards />
            ) : (
                <div className="grid gap-4 xl:grid-cols-2">
                    {companies.map((company) => (
                        <CompanyCard key={company.id || company.symbol} company={company} />
                    ))}
                </div>
            )}

            {!loading && companies.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
                    <div className="text-lg font-bold text-slate-950">No companies found</div>
                    <div className="mt-2 text-sm font-medium text-slate-500">Adjust the filters to broaden the result set.</div>
                </div>
            ) : null}

            <div className="flex justify-center rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <Pagination page={page} pages={pages} onPageChange={setPage} loading={loading} />
            </div>
        </div>
    );
};
